import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PacksSection from "../components/PacksSection";
import MyInvestments from "../components/MyInvestments";
import PayoutHistory from "../components/PayoutHistory";
import { useAuth } from "../hooks/useAuth";
import { Zap, ShieldCheck, Cpu, TrendingUp, DollarSign, ArrowRight, BarChart3, Wallet, Sparkles, Lock } from "lucide-react";

export default function Packs() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [globalStats, setGlobalStats] = React.useState(null);

  React.useEffect(() => {
    import("../services/api").then(({ default: api }) => {
      api.get("/public/stats").then(res => {
        if (res.success) setGlobalStats(res.data);
      });
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#060a14] text-white font-inter">
      <Header />
      
      {/* Hero Section with 3D Background */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
        {/* 3D Depth Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-30%] left-[5%] w-[700px] h-[700px] bg-indigo-600/8 rounded-full blur-[150px]" />
          <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] bg-blue-500/6 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-[30%] w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[130px]" />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 md:gap-8 mb-10 md:mb-14">
            <div className="space-y-4 md:space-y-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 backdrop-blur-sm">
                <BarChart3 size={14} className="text-indigo-400 animate-pulse" />
                <span className="font-black text-[10px] text-indigo-400 uppercase tracking-[0.2em]">{t('investments.nova_assets_mgmt')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] font-outfit">
                <span className="text-white">{t('investments.investment_center').split(' ').slice(0, -1).join(' ')} </span>
                <span className="bg-gradient-to-r from-indigo-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">{t('investments.investment_center').split(' ').pop()}</span>
              </h1>
              <p className="text-slate-400 max-w-lg font-medium leading-relaxed text-sm md:text-base">
                {t('investments.subtitle')}
              </p>
            </div>
            
            <Link to="/wallet" className="flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:border-indigo-500/30 transition-all duration-300 backdrop-blur-sm group">
              <Wallet size={18} className="text-indigo-400 group-hover:scale-110 transition-transform" />
              <span className="font-black text-[11px] uppercase tracking-widest text-slate-300">{t('investments.my_portfolio')}</span>
            </Link>
          </div>

          {/* Quick Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: t('investments.avg_yield'), value: "+2.4% / Day", icon: TrendingUp, color: "text-emerald-400", glow: "shadow-emerald-500/10", bg: "from-emerald-500/10" },
              { label: t('investments.security_active'), value: "100% Active", icon: ShieldCheck, color: "text-indigo-400", glow: "shadow-indigo-500/10", bg: "from-indigo-500/10" },
              { label: t('investments.exec_time'), value: "<1ms", icon: Zap, color: "text-cyan-400", glow: "shadow-cyan-500/10", bg: "from-cyan-500/10" },
              { label: t('investments.assets_under_mgmt'), value: globalStats?.invested || "$12.8M+", icon: DollarSign, color: "text-amber-400", glow: "shadow-amber-500/10", bg: "from-amber-500/10" }
            ].map((stat, i) => (
              <div key={i} className={`p-4 rounded-2xl bg-gradient-to-br ${stat.bg} to-transparent border border-white/[0.06] backdrop-blur-sm hover:border-white/[0.12] transition-all duration-300 hover:-translate-y-0.5 shadow-lg ${stat.glow}`}>
                <div className={`p-2 rounded-xl bg-[#0c1220] border border-white/[0.06] w-fit mb-3 ${stat.color}`}>
                  <stat.icon size={18} />
                </div>
                <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.15em] mb-0.5">{stat.label}</p>
                <p className="text-sm font-black text-white font-outfit">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User's Active Investments */}
      <section className="relative z-10 pb-12">
        <MyInvestments />
      </section>

      {/* Available Plans */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-transparent to-transparent pointer-events-none" />
        <PacksSection />
      </div>

      {/* Payout History */}
      <section className="relative py-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <PayoutHistory />
        </div>
      </section>

      {/* Architecture Section with 3D Cards */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[20%] right-[5%] w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-[10%] left-[10%] w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="space-y-10">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-5xl font-black font-outfit uppercase tracking-tight leading-none">
                  {t('investments.nova_architecture')}
                </h2>
                <div className="w-16 h-1 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full" />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Zap, title: t('investments.hft_algo'), desc: t('investments.hft_desc'), color: "indigo", borderHover: "hover:border-indigo-500/30" },
                  { icon: Cpu, title: t('investments.predictive_ai'), desc: t('investments.predictive_ai_desc'), color: "cyan", borderHover: "hover:border-cyan-500/30" },
                  { icon: ShieldCheck, title: t('investments.smart_exit'), desc: t('investments.smart_exit_desc'), color: "emerald", borderHover: "hover:border-emerald-500/30" },
                  { icon: DollarSign, title: t('investments.protected_capital'), desc: t('investments.protected_capital_desc'), color: "amber", borderHover: "hover:border-amber-500/30" }
                ].map((item, i) => (
                  <div key={i} className={`group p-5 rounded-2xl bg-[#0c1220] border border-white/[0.04] ${item.borderHover} transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}>
                    <div className={`p-2.5 bg-${item.color}-500/10 rounded-xl w-fit mb-3 text-${item.color}-400 group-hover:scale-110 transition-transform`}>
                      <item.icon size={20} />
                    </div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-white mb-1.5">{item.title}</h4>
                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 3D Shield Card */}
            <div className="relative p-8 md:p-14 rounded-[32px] bg-gradient-to-br from-[#0f1629] via-[#111d35] to-[#0a1225] border border-white/[0.06] overflow-hidden group shadow-[0_20px_60px_rgba(0,0,0,0.5)] hover:shadow-[0_30px_80px_rgba(99,102,241,0.15)] transition-all duration-500 hover:-translate-y-1">
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] group-hover:bg-indigo-500/20 transition-all duration-700 pointer-events-none" />
              <div className="absolute bottom-[-50px] left-[-50px] w-[200px] h-[200px] bg-cyan-500/5 rounded-full blur-[80px] pointer-events-none" />
              
              <div className="relative z-10 space-y-8">
                <div className="p-4 bg-[#0c1220] border border-white/[0.06] rounded-2xl w-fit shadow-2xl">
                  <ShieldCheck size={40} className="text-indigo-400" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black font-outfit uppercase tracking-tight">{t('investments.nova_shield_guarantee')}</h3>
                <p className="text-slate-400 font-medium leading-relaxed text-sm md:text-base italic border-l-2 border-indigo-500/30 pl-4">
                  "{t('investments.nova_shield_desc')}"
                </p>
                <div className="pt-6 border-t border-white/[0.06] space-y-3">
                  {[
                    { text: t('investments.asset_protection_active'), pulse: true },
                    { text: t('investments.verified_reserve'), pulse: false }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full bg-emerald-500 ${item.pulse ? 'animate-pulse' : ''}`} />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern CTA */}
      <section className="py-16 md:py-24 relative">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="relative rounded-[32px] bg-gradient-to-br from-indigo-600/20 via-[#111d35] to-purple-600/10 border border-indigo-500/20 p-10 md:p-20 text-center overflow-hidden shadow-[0_40px_80px_-16px_rgba(99,102,241,0.15)]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-cyan-500/8 rounded-full blur-[100px] pointer-events-none" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 mb-8">
                <Sparkles size={14} className="text-indigo-400" />
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Premium Access</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl font-black font-outfit uppercase tracking-tight mb-6 leading-tight">
                <span className="text-white">{t('investments.optimize_passive').split(' ').slice(0, 2).join(' ')} </span>
                <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">{t('investments.optimize_passive').split(' ').slice(2).join(' ')}</span>
              </h2>
              <p className="text-slate-400 font-medium mb-10 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed">{t('investments.investor_trust')}</p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!isAuthenticated ? (
                  <Link to="/register" className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all duration-300 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5 group">
                    {t('investments.open_pro_account')}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <Link to="/dashboard" className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all duration-300 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5 group">
                    {t('nav.dashboard')}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
                <Link to="/contact" className="inline-flex items-center gap-2 px-10 py-4 rounded-2xl border border-white/[0.08] hover:bg-white/[0.04] hover:border-white/[0.15] text-slate-300 font-black text-xs uppercase tracking-widest transition-all duration-300">
                  <Lock size={14} />
                  {t('investments.priority_support')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}